import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trainingCategories } from '../constants/sessionCategories';

interface SessionFormFieldsProps {
  title: string;
  onTitleChange: (value: string) => void;
  customTitle: string;
  setCustomTitle: (value: string) => void;
  ageGroup: 'all' | 'kids' | 'teens' | 'adults';
  setAgeGroup: (value: 'all' | 'kids' | 'teens' | 'adults') => void;
  date: string;
  setDate: (value: string) => void;
  maxParticipants: string;
  setMaxParticipants: (value: string) => void;
  startTime: string;
  setStartTime: (value: string) => void;
  endTime: string;
  setEndTime: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  sessionType: 'in_person' | 'online' | 'hybrid';
  setSessionType: (value: 'in_person' | 'online' | 'hybrid') => void;
  onlineMeetingUrl: string;
  setOnlineMeetingUrl: (value: string) => void;
  meetingPlatform: string;
  setMeetingPlatform: (value: string) => void;
  meetingPassword: string;
  setMeetingPassword: (value: string) => void;
}

const SessionFormFields: React.FC<SessionFormFieldsProps> = ({
  title,
  onTitleChange,
  customTitle,
  setCustomTitle,
  ageGroup,
  setAgeGroup,
  date,
  setDate,
  maxParticipants,
  setMaxParticipants,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  description,
  setDescription,
  sessionType,
  setSessionType,
  onlineMeetingUrl,
  setOnlineMeetingUrl,
  meetingPlatform,
  setMeetingPlatform,
  meetingPassword,
  setMeetingPassword
}) => {
  useUiLanguage();
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">{uiText("Session Type *")}</Label>
        <Select value={title} onValueChange={onTitleChange}>
          <SelectTrigger>
            <SelectValue placeholder={uiText("Select session type")} />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(trainingCategories).map(([category, options]) => (
              <div key={category}>
                <div className="px-2 py-1 text-sm font-semibold text-gray-500 bg-gray-100">
                  {category}
                </div>
                {options.map((option) => (
                  <SelectItem key={option} value={option} className="pl-4">
                    {option}
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>

      {title === 'Custom' && (
        <div>
          <Label htmlFor="customTitle">{uiText("Custom Session Title *")}</Label>
          <Input
            id="customTitle"
            placeholder={uiText("Enter custom session title")}
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
          />
        </div>
      )}

      <div>
        <Label htmlFor="ageGroup">{uiText("Target Age Group *")}</Label>
        <Select value={ageGroup} onValueChange={setAgeGroup}>
          <SelectTrigger>
            <SelectValue placeholder={uiText("Select age group")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{uiText("All Ages")}</SelectItem>
            <SelectItem value="kids">{uiText("Kids (5-12)")}</SelectItem>
            <SelectItem value="teens">{uiText("Teens (13-17)")}</SelectItem>
            <SelectItem value="adults">{uiText("Adults (18+)")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="sessionType">{uiText("Session Type *")}</Label>
        <Select value={sessionType} onValueChange={setSessionType}>
          <SelectTrigger>
            <SelectValue placeholder={uiText("Select session type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="in_person">{uiText("In-Person")}</SelectItem>
            <SelectItem value="online">{uiText("Online")}</SelectItem>
            <SelectItem value="hybrid">{uiText("Hybrid")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(sessionType === 'online' || sessionType === 'hybrid') && (
        <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
          <h4 className="font-medium text-sm">{uiText("Online Meeting Details")}</h4>

          <div>
            <Label htmlFor="meetingPlatform">{uiText("Meeting Platform")}</Label>
            <Select value={meetingPlatform} onValueChange={setMeetingPlatform}>
              <SelectTrigger>
                <SelectValue placeholder={uiText("Select platform")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="zoom">{uiText("Zoom")}</SelectItem>
                <SelectItem value="google_meet">{uiText("Google Meet")}</SelectItem>
                <SelectItem value="teams">{uiText("Microsoft Teams")}</SelectItem>
                <SelectItem value="other">{uiText("Other")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="onlineMeetingUrl">{uiText("Meeting URL")}</Label>
            <Input
              id="onlineMeetingUrl"
              placeholder={uiText('https://zoom.us/j/... or https://meet.google.com/...')}
              value={onlineMeetingUrl}
              onChange={(e) => setOnlineMeetingUrl(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="meetingPassword">{uiText("Meeting Password (Optional)")}</Label>
            <Input
              id="meetingPassword"
              placeholder={uiText("Enter meeting password")}
              value={meetingPassword}
              onChange={(e) => setMeetingPassword(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="date">{uiText("Date *")}</Label>
          <Input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="maxParticipants">{uiText("Max Participants")}</Label>
          <Input
            id="maxParticipants"
            type="number"
            placeholder={uiText('e.g., 15')}
            value={maxParticipants}
            onChange={(e) => setMaxParticipants(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="startTime">{uiText("Start Time *")}</Label>
          <Input
            id="startTime"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="endTime">{uiText("End Time *")}</Label>
          <Input
            id="endTime"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="description">{uiText("Description (Optional)")}</Label>
        <Textarea
          id="description"
          placeholder={uiText("Additional details about the session...")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>
    </div>
  );
};

export default SessionFormFields;
